import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Pill } from "./ui/pill";
import { Button } from "./ui/button";
import { useBranchAccessRequestDetail } from "../hooks/queries";
import { grantBranchAccessRequest, denyBranchAccessRequest } from "../api/api";

interface BranchAccessRequestPopupProps {
  requestId: number | null;
  onClose: () => void;
}

export default function BranchAccessRequestPopup({ requestId, onClose }: BranchAccessRequestPopupProps) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useBranchAccessRequestDetail(requestId ?? undefined);
  const [acting, setActing] = useState<"grant" | "deny" | null>(null);
  const [error, setError] = useState("");

  async function handleResolve(action: "grant" | "deny") {
    if (!requestId) return;
    setActing(action);
    setError("");
    try {
      const resolve = action === "grant" ? grantBranchAccessRequest : denyBranchAccessRequest;
      await resolve(requestId, { silenceErrors: true });
      queryClient.invalidateQueries({ queryKey: ["branch-requests", "detail", requestId] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Actie mislukt");
    } finally {
      setActing(null);
    }
  }

  return (
    <Dialog open={requestId != null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl">Toegangsverzoek</DialogTitle>
        </DialogHeader>

        {isLoading && <p className="py-4 text-sm text-waves-muted">Laden…</p>}
        {isError && <p className="py-4 text-sm font-medium text-status-alert-fg">Kon het verzoek niet laden.</p>}

        {data && data.status === "pending" && (
          <div className="flex flex-col gap-4">
            <p className="text-[15px] leading-relaxed text-waves-body">
              <span className="font-bold text-waves-ink">{data.user_name}</span> vraagt toegang tot vestiging{" "}
              <span className="font-bold text-waves-ink">{data.branch_name}</span>.
            </p>
            {error && (
              <div className="rounded-nav bg-status-alert-bg px-4 py-2.5 text-sm font-medium text-status-alert-fg">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="destructive" onClick={() => handleResolve("deny")} disabled={acting !== null}>
                {acting === "deny" ? "Afwijzen…" : "Afwijzen"}
              </Button>
              <Button variant="ink" onClick={() => handleResolve("grant")} disabled={acting !== null}>
                {acting === "grant" ? "Toegang geven…" : "Toegang geven"}
              </Button>
            </div>
          </div>
        )}

        {data && data.status !== "pending" && (
          <div className="flex flex-col gap-3">
            <p className="text-[15px] leading-relaxed text-waves-body">
              Verzoek van <span className="font-bold text-waves-ink">{data.user_name}</span> voor vestiging{" "}
              <span className="font-bold text-waves-ink">{data.branch_name}</span>.
            </p>
            <Pill kind={data.status === "fulfilled" ? "health-ok" : "health-error"} className="self-start">
              {data.status === "fulfilled" ? "Toegang verleend" : "Afgewezen"}
            </Pill>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
